import React, { useRef, useState } from 'react';
import { Button, Form, Message, Table } from 'semantic-ui-react';
import { client } from '../../shared/client';
import './recovery.scss';

type Sale = {
  id: number;
  sale_status: string;
  fail_status_reason?: string;
  created_time: string;
  sn: string;
  model: string;
  receipt_id?: number;
  receipt_status?: string;
  receipt_date?: string;
  fn?: string;
  seller_first_name?: string;
  seller_last_name?: string;
  guarantee_expire?: string;
  seller_id?: number;
  buyer_id?: number;
  fixed_seller_bonus?: number;
};
type Preview = {
  sale: Sale;
  token: string;
  canApply: boolean;
  hasChanges: boolean;
  serviceReceipt: boolean;
  guaranteeAfter?: string;
  insertToolEvent: boolean;
  problems: string[];
};

const display = (value: any) =>
  value === null || value === undefined || value === '' ? '—' : String(value);
// Server timestamps are the system's civil timestamps, not browser-local times.
const date = (value?: string) =>
  value ? value.replace('T', ' ').slice(0, 19) : '—';
const errorText = (e: any) =>
  (e.response && e.response.data && e.response.data.message) ||
  'Не удалось выполнить запрос. Обновите расчёт перед повторной попыткой.';

export default function SaleRecovery() {
  const [query, setQuery] = useState('');
  const [sales, setSales] = useState<Sale[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [searched, setSearched] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const request = useRef(0);
  const scenario = 'buyer';
  const base = '/service/sales/recovery';

  const load = async (id: number) => {
    const seq = ++request.current;
    setBusy(true);
    setError('');
    setSuccess(false);
    setPreview(null);
    setConfirmed(false);
    try {
      const response = await client.get(`${base}/${scenario}/${id}`);
      if (seq === request.current) {
        setPreview(response.data);
      }
    } catch (e) {
      if (seq === request.current) setError(errorText(e));
    } finally {
      if (seq === request.current) setBusy(false);
    }
  };
  const search = async () => {
    const seq = ++request.current;
    setBusy(true);
    setError('');
    setSuccess(false);
    setPreview(null);
    setSales([]);
    setConfirmed(false);
    setSearched(false);
    try {
      const response = await client.get(`${base}/search`, {
        params: { query: query.trim() },
      });
      if (seq !== request.current) return;
      setSales(response.data);
      setSearched(true);
      if (response.data.length === 1) await load(response.data[0].id);
    } catch (e) {
      if (seq === request.current) setError(errorText(e));
    } finally {
      if (seq === request.current) setBusy(false);
    }
  };
  const apply = async () => {
    if (!preview || !confirmed || busy) return;
    setBusy(true);
    setError('');
    try {
      const response = await client.post(
        `${base}/${scenario}/${preview.sale.id}`,
        { token: preview.token }
      );
      setPreview(response.data);
      setSuccess(true);
    } catch (e) {
      setError(errorText(e));
      // A lost response can hide a committed operation. Always recalculate before retrying.
      setPreview(null);
    } finally {
      setConfirmed(false);
      setBusy(false);
    }
  };

  return (
    <section className="sale-recovery">
      <h1>Исправление продажи покупателя</h1>
      <p>
        Перевод продажи в FINISH и очистка причины ошибки. Баллы, гарантия и чек
        не изменяются.
      </p>
      <Form onSubmit={search}>
        <Form.Group>
          <Form.Input
            label="ID продажи или серийный номер"
            value={query}
            maxLength={100}
            disabled={busy}
            onChange={(_, data) => setQuery(String(data.value))}
          />
          <Form.Field className="search-action">
            <Button
              primary
              type="submit"
              disabled={busy || !query.trim()}
              loading={busy}
            >
              Найти
            </Button>
          </Form.Field>
        </Form.Group>
      </Form>
      {error && <Message negative>{error}</Message>}
      {success && (
        <Message positive>
          Изменения применены. Ниже показаны актуальные данные продажи.
        </Message>
      )}
      {searched && !sales.length && <Message>Продажи не найдены.</Message>}
      {sales.length > 1 && (
        <>
          <p>
            Найдено {sales.length}
            {sales.length === 100 ? ' (показаны первые 100)' : ''}. Выберите
            продажу.
          </p>
          <Table compact celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>ID</Table.HeaderCell>
                <Table.HeaderCell>Дата</Table.HeaderCell>
                <Table.HeaderCell>Инструмент</Table.HeaderCell>
                <Table.HeaderCell>Статус</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sales.map((s) => (
                <Table.Row key={s.id}>
                  <Table.Cell>{s.id}</Table.Cell>
                  <Table.Cell>{date(s.created_time)}</Table.Cell>
                  <Table.Cell>
                    {s.sn} · {s.model}
                  </Table.Cell>
                  <Table.Cell>{s.sale_status}</Table.Cell>
                  <Table.Cell>
                    <Button disabled={busy} onClick={() => load(s.id)}>
                      Открыть
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
      {preview && (
        <>
          <h2>Продажа №{preview.sale.id}</h2>
          <dl>
            <dt>Инструмент</dt>
            <dd>
              {display(preview.sale.model)} · {display(preview.sale.sn)}
            </dd>
            <dt>Дата в системе</dt>
            <dd>{date(preview.sale.created_time)}</dd>
            <dt>Статус</dt>
            <dd>{preview.sale.sale_status}</dd>
            <dt>Причина ошибки</dt>
            <dd>{display(preview.sale.fail_status_reason)}</dd>
            <dt>Чек</dt>
            <dd>
              №{display(preview.sale.receipt_id)} ·{' '}
              {display(preview.sale.receipt_status)}
              {preview.serviceReceipt ? ' · Служебный' : ''}
            </dd>
            <dt>Продавец / покупатель</dt>
            <dd>
              {display(preview.sale.seller_id)} /{' '}
              {display(preview.sale.buyer_id)}
            </dd>
          </dl>
          {preview.problems.length > 0 && (
            <Message
              warning
              header="Исправление недоступно"
              list={preview.problems}
            />
          )}
          {preview.canApply && (
            <>
              <h2>Изменения перед применением</h2>
              <p>
                Статус: {preview.sale.sale_status} → FINISH. Причина ошибки
                будет очищена.
              </p>
              <Form.Checkbox
                label="Проверил продажу и перечисленные изменения"
                checked={confirmed}
                disabled={busy}
                onChange={(_, data) => setConfirmed(!!data.checked)}
              />
              <Button
                primary
                disabled={busy || !confirmed}
                loading={busy}
                onClick={apply}
              >
                Применить исправление
              </Button>
            </>
          )}
          {!preview.hasChanges && preview.problems.length === 0 && (
            <Message>Исправление не требуется.</Message>
          )}
          <Button basic disabled={busy} onClick={() => load(preview.sale.id)}>
            Обновить расчёт
          </Button>
        </>
      )}
    </section>
  );
}
